import React, { useState, useEffect, useRef } from 'react'
import { 
  FileText, Download, Trash2, Upload, Eye, EyeOff,
  File, FileSpreadsheet, FileImage, AlertCircle, X, Lock,
  Shield, Loader2, ChevronDown, ChevronUp, Check
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from 'sonner'
import ExcelPreviewModal from './ExcelPreviewModal'

const API_URL = process.env.REACT_APP_BACKEND_URL

const TYPE_DOCUMENT_CONFIG = {
  constat_amiable: { label: 'Constat amiable', icon: FileText, color: 'bg-red-100 text-red-600', categorie: 'sinistre' },
  photos_degats: { label: 'Photos des dégâts', icon: FileImage, color: 'bg-purple-100 text-purple-600', categorie: 'sinistre' },
  facture_reparation: { label: 'Facture réparation', icon: FileSpreadsheet, color: 'bg-green-100 text-green-600', categorie: 'sinistre' },
  devis: { label: 'Devis', icon: FileSpreadsheet, color: 'bg-blue-100 text-blue-600', categorie: 'sinistre' },
  rapport_expert: { label: "Rapport d'expertise", icon: FileText, color: 'bg-amber-100 text-amber-600', categorie: 'sinistre' },
  pv_police: { label: 'PV de police', icon: FileText, color: 'bg-red-100 text-red-700', categorie: 'sinistre' },
  declaration_sinistre: { label: 'Déclaration de sinistre', icon: FileText, color: 'bg-slate-100 text-slate-600', categorie: 'sinistre' },
  correspondance_assureur: { label: 'Correspondance assureur', icon: File, color: 'bg-cyan-100 text-cyan-600', categorie: 'sinistre' },
  police_assurance: { label: "Police d'assurance", icon: FileText, color: 'bg-indigo-100 text-indigo-600', categorie: 'police' },
  attestation_assurance: { label: 'Attestation', icon: FileText, color: 'bg-teal-100 text-teal-600', categorie: 'police' },
  note_analyse: { label: "Note d'analyse C2", icon: FileText, color: 'bg-slate-200 text-slate-700', categorie: 'interne' },
  autre: { label: 'Autre', icon: File, color: 'bg-slate-100 text-slate-500', categorie: 'autre' },
}

const CATEGORY_LABELS = {
  sinistre: 'Documents sinistre',
  police: 'Documents police',
  administratif: 'Documents administratifs',
  interne: 'Documents internes C2',
  autre: 'Autres'
}

function getVisibilityBadge(visibilite, confidentiel) {
  if (confidentiel) return { color: 'bg-red-100 text-red-800 border-red-200', label: 'Confidentiel', icon: Lock }
  if (visibilite === 'c2_only') return { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'C2 seul', icon: Shield }
  if (visibilite === 'daf_c2') return { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'DAF+C2', icon: Eye }
  return { color: 'bg-green-100 text-green-800 border-green-200', label: 'Tous', icon: Eye }
}

function formatFileSize(bytes) {
  if (!bytes) return '—'
  const kb = bytes / 1024
  return kb < 1024 ? `${kb.toFixed(1)} Ko` : `${(kb / 1024).toFixed(1)} Mo`
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function isExcelFile(filename) {
  const lower = (filename || '').toLowerCase()
  return lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv')
}

function isImageFile(filename, mimeType) {
  if (mimeType?.startsWith('image/')) return true
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic|heif)$/i.test(filename || '')
}

function isPdfFile(filename, mimeType) {
  if (mimeType === 'application/pdf') return true
  return /\.pdf$/i.test(filename || '')
}

function guessDocumentType(filename) {
  const lower = (filename || '').toLowerCase()
  if (lower.includes('constat')) return 'constat_amiable'
  if (lower.includes('devis')) return 'devis'
  if (lower.includes('facture')) return 'facture_reparation'
  if (lower.includes('expertise') || lower.includes('rapport')) return 'rapport_expert'
  if (lower.includes('pv')) return 'pv_police'
  if (/\.(jpg|jpeg|png|webp|heic|gif)$/i.test(lower)) return 'photos_degats'
  return 'autre'
}

async function getSignedUrl(docId) {
  const res = await fetch(`${API_URL}/api/documents/${docId}/download-url`)
  if (!res.ok) throw new Error('Erreur URL signée')
  const data = await res.json()
  return data.url
}

export default function SinistreDocuments({ 
  documents: initialDocuments, 
  sinistreId, 
  tenantId,
  onRefresh, 
  canUpload = true,
  userRole = 'declarant'
}) {
  const { profile } = useAuth()
  const [documents, setDocuments] = useState(initialDocuments || [])
  const [typesDocuments, setTypesDocuments] = useState([])
  const [uploading, setUploading] = useState(false)
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [expandedDoc, setExpandedDoc] = useState(null)
  const [error, setError] = useState(null)

  // Preview states
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewType, setPreviewType] = useState(null) // 'image' | 'pdf'
  const [previewFilename, setPreviewFilename] = useState('')
  const [excelPreview, setExcelPreview] = useState(null)

  // Upload form state
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedType, setSelectedType] = useState('')
  const [description, setDescription] = useState('')
  const [visibilite, setVisibilite] = useState('tous')
  const [confidentiel, setConfidentiel] = useState(false)
  const fileInputRef = useRef(null)

  const effectiveRole = userRole || profile?.role || 'declarant'
  const isC2 = effectiveRole === 'c2_admin' || effectiveRole === 'c2_gestionnaire' || effectiveRole === 'master_admin'
  const canDelete = isC2
  const canChangeVisibility = isC2
  const effectiveTenantId = tenantId || profile?.tenant_id || ''

  useEffect(() => {
    fetch(`${API_URL}/api/documents/types`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setTypesDocuments(data.types || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (initialDocuments) setDocuments(initialDocuments)
  }, [initialDocuments])

  // ============== UPLOAD ==============
  async function doUpload(file, typeDoc) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('sinistre_id', sinistreId || '')
    formData.append('tenant_id', effectiveTenantId)
    formData.append('type_document', typeDoc || guessDocumentType(file.name))
    formData.append('visibilite', visibilite)
    formData.append('confidentiel', String(confidentiel))
    if (description) formData.append('description', description)
    if (profile?.id) formData.append('uploaded_by', profile.id)

    const response = await fetch(`${API_URL}/api/documents/upload`, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      let detail = `Erreur serveur (${response.status})`
      try {
        const errData = await response.json()
        if (errData.detail) detail = typeof errData.detail === 'string' ? errData.detail : JSON.stringify(errData.detail)
      } catch { /* ignore */ }
      throw new Error(detail)
    }
    return response.json()
  }

  async function handleUpload(e) {
    e.preventDefault()
    if (!selectedFile) return
    setUploading(true)
    setError(null)
    try {
      await doUpload(selectedFile, selectedType || 'autre')
      toast.success('Document ajouté avec succès')
      resetForm()
      onRefresh?.()
    } catch (err) {
      console.error('Upload error:', err)
      toast.error(err.message || "Erreur lors de l'upload")
      setError(err.message)
    } finally {
      setUploading(false)
    }
  }

  async function handleDropUpload(e) {
    const files = Array.from(e.target.files)
    if (!files.length) return
    setUploading(true)
    let successCount = 0
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} dépasse 50 Mo`)
        continue
      }
      try {
        await doUpload(file, guessDocumentType(file.name))
        successCount++
      } catch (err) {
        toast.error(`Erreur upload ${file.name}: ${err.message}`)
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} document(s) ajouté(s)`)
      onRefresh?.()
    }
    setUploading(false)
  }

  function resetForm() {
    setSelectedFile(null)
    setSelectedType('')
    setDescription('')
    setVisibilite('tous')
    setConfidentiel(false)
    setShowUploadForm(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // ============== PREVIEW ==============
  async function handlePreview(doc) {
    const filename = doc.nom_fichier || ''

    // Excel/CSV → dedicated modal
    if (isExcelFile(filename)) {
      setExcelPreview({ id: doc.id, filename })
      return
    }

    try {
      const url = await getSignedUrl(doc.id)

      // Images → inline modal
      if (isImageFile(filename, doc.mime_type)) {
        setPreviewUrl(url)
        setPreviewType('image')
        setPreviewFilename(filename)
        return
      }

      // PDF → inline iframe modal
      if (isPdfFile(filename, doc.mime_type)) {
        setPreviewUrl(url)
        setPreviewType('pdf')
        setPreviewFilename(filename)
        return
      }

      // Everything else → open in new tab for browser-native preview
      window.open(url, '_blank')
    } catch (err) {
      console.error('Preview error:', err)
      toast.error('Erreur lors de la prévisualisation')
    }
  }

  // ============== DOWNLOAD ==============
  async function handleDownload(doc) {
    try {
      const url = await getSignedUrl(doc.id)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.nom_fichier
      a.target = '_blank'
      a.click()
    } catch {
      toast.error('Erreur lors du téléchargement')
    }
  }

  // ============== DELETE ==============
  async function handleDelete(doc) {
    if (!window.confirm('Supprimer ce document ?')) return
    try {
      const res = await fetch(`${API_URL}/api/documents/${doc.id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error()
      toast.success('Document supprimé')
      onRefresh?.()
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  // ============== VISIBILITY ==============
  async function handleVisibilityChange(doc, newVisibilite, newConfidentiel) {
    try {
      const res = await fetch(`${API_URL}/api/documents/${doc.id}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibilite: newVisibilite, confidentiel: newConfidentiel })
      })
      if (res.ok) { toast.success('Visibilité mise à jour'); onRefresh?.() }
    } catch { toast.error('Erreur lors de la mise à jour') }
  }

  // ============== TYPE CONFIG ==============
  const getTypeConfig = (typeCode) => {
    const apiType = typesDocuments.find(t => t.code === typeCode)
    if (apiType) {
      return {
        label: apiType.libelle,
        icon: TYPE_DOCUMENT_CONFIG[typeCode]?.icon || File,
        color: TYPE_DOCUMENT_CONFIG[typeCode]?.color || 'bg-slate-100 text-slate-600',
        categorie: apiType.categorie
      }
    }
    return TYPE_DOCUMENT_CONFIG[typeCode] || TYPE_DOCUMENT_CONFIG.autre
  }

  const filteredTypes = typesDocuments.length > 0 
    ? typesDocuments.filter(t => {
        if (isC2) return true
        if (effectiveRole === 'daf') return t.visibilite_defaut !== 'c2_only'
        return t.visibilite_defaut === 'tous'
      })
    : Object.entries(TYPE_DOCUMENT_CONFIG)
        .filter(([_, config]) => isC2 || config.categorie !== 'interne')
        .map(([code, config]) => ({ code, libelle: config.label, categorie: config.categorie }))

  const documentsByCategory = documents.reduce((acc, doc) => {
    const config = getTypeConfig(doc.type_document)
    const category = config.categorie || 'autre'
    if (!acc[category]) acc[category] = []
    acc[category].push({ ...doc, typeConfig: config })
    return acc
  }, {})

  // ============== RENDER ==============
  if (!documents?.length) {
    return (
      <div className="text-center py-8" data-testid="sinistre-documents-empty">
        <FileText size={32} className="mx-auto text-slate-300 mb-2" />
        <p className="text-sm text-slate-400 mb-4">Aucun document</p>
        {canUpload && (
          <button onClick={() => setShowUploadForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            data-testid="add-document-btn">
            <Upload size={16} /> Ajouter un document
          </button>
        )}
        {showUploadForm && (
          <UploadFormModal show onClose={() => setShowUploadForm(false)} onSubmit={handleUpload}
            filteredTypes={filteredTypes} isC2={isC2} uploading={uploading}
            selectedFile={selectedFile} setSelectedFile={setSelectedFile}
            selectedType={selectedType} setSelectedType={setSelectedType}
            description={description} setDescription={setDescription}
            visibilite={visibilite} setVisibilite={setVisibilite}
            confidentiel={confidentiel} setConfidentiel={setConfidentiel}
            fileInputRef={fileInputRef} typesDocuments={typesDocuments} />
        )}
      </div>
    )
  }

  return (
    <div data-testid="sinistre-documents">
      {canUpload && (
        <div className="mb-4 flex justify-between items-center">
          <span className="text-sm text-slate-500">{documents.length} document(s)</span>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 text-slate-600 text-sm rounded-lg hover:border-indigo-400 hover:text-indigo-600 cursor-pointer transition-colors"
              data-testid="quick-upload-label">
              <Upload size={14} />
              Glisser / cliquer
              <input type="file" multiple className="hidden" onChange={handleDropUpload} disabled={uploading}
                data-testid="quick-upload-input" />
            </label>
            <button onClick={() => setShowUploadForm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="add-document-btn">
              <Upload size={16} /> Ajouter
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {uploading && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center gap-2 text-indigo-700 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Upload en cours...
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(documentsByCategory).map(([category, docs]) => (
          <div key={category}>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {CATEGORY_LABELS[category] || category}
            </h4>
            <div className="space-y-2">
              {docs.map((doc) => (
                <DocumentRow key={doc.id} doc={doc} expandedDoc={expandedDoc}
                  setExpandedDoc={setExpandedDoc} onPreview={handlePreview}
                  onDownload={handleDownload} onDelete={handleDelete}
                  onVisibilityChange={handleVisibilityChange}
                  canDelete={canDelete} canChangeVisibility={canChangeVisibility} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Upload modal */}
      {showUploadForm && (
        <UploadFormModal show onClose={() => setShowUploadForm(false)} onSubmit={handleUpload}
          filteredTypes={filteredTypes} isC2={isC2} uploading={uploading}
          selectedFile={selectedFile} setSelectedFile={setSelectedFile}
          selectedType={selectedType} setSelectedType={setSelectedType}
          description={description} setDescription={setDescription}
          visibilite={visibilite} setVisibilite={setVisibilite}
          confidentiel={confidentiel} setConfidentiel={setConfidentiel}
          fileInputRef={fileInputRef} typesDocuments={typesDocuments} />
      )}

      {/* Universal preview modal (image + PDF) */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => { setPreviewUrl(null); setPreviewType(null) }}>
          <button onClick={() => { setPreviewUrl(null); setPreviewType(null) }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors z-50">
            <X size={20} className="text-white" />
          </button>
          <div className="absolute top-4 left-4 text-white text-sm bg-black/40 px-3 py-1 rounded-lg truncate max-w-xs">
            {previewFilename}
          </div>
          <div className="w-full h-full max-w-6xl max-h-[90vh] flex items-center justify-center"
            onClick={e => e.stopPropagation()}>
            {previewType === 'image' && (
              <img src={previewUrl} alt={previewFilename}
                className="max-w-full max-h-full object-contain rounded-lg" />
            )}
            {previewType === 'pdf' && (
              <iframe src={previewUrl} title={previewFilename}
                className="w-full h-full rounded-lg bg-white" style={{ minHeight: '80vh' }} />
            )}
          </div>
        </div>
      )}

      {/* Excel preview modal */}
      {excelPreview && (
        <ExcelPreviewModal documentId={excelPreview.id} filename={excelPreview.filename}
          onClose={() => setExcelPreview(null)} />
      )}
    </div>
  )
}

// ============== Document Row ==============
function DocumentRow({ doc, expandedDoc, setExpandedDoc, onPreview, onDownload, onDelete, onVisibilityChange, canDelete, canChangeVisibility }) {
  const config = doc.typeConfig
  const Icon = config.icon
  const visibility = getVisibilityBadge(doc.visibilite, doc.confidentiel)
  const VisIcon = visibility.icon
  const isExpanded = expandedDoc === doc.id
  const filename = doc.nom_fichier || ''

  // Determine preview badge
  let previewBadge = null
  if (isExcelFile(filename)) previewBadge = 'Excel'
  else if (isPdfFile(filename, doc.mime_type)) previewBadge = 'PDF'
  else if (isImageFile(filename, doc.mime_type)) previewBadge = 'Image'

  return (
    <div className="border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
      data-testid={`document-${doc.id}`}>
      <div className="flex items-center gap-3 p-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${config.color}`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-800 truncate">{filename}</span>
            {(doc.visibilite !== 'tous' || doc.confidentiel) && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-full border ${visibility.color}`}>
                <VisIcon className="w-3 h-3" /> {visibility.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
            <span className={`px-1.5 py-0.5 rounded ${config.color}`}>{config.label}</span>
            {doc.origin === 'pre_declaration' && (
              <><span>•</span><span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 font-medium">Pre-declaration</span></>
            )}
            <span>•</span>
            <span>{formatFileSize(doc.taille || doc.taille_octets)}</span>
            <span>•</span>
            <span>{formatDate(doc.created_at)}</span>
            {previewBadge && (
              <><span>•</span><span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-600 font-medium">{previewBadge}</span></>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onPreview(doc)}
            className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center transition-colors" title="Prévisualiser"
            data-testid={`preview-${doc.id}`}>
            <Eye size={16} className="text-slate-500" />
          </button>
          <button onClick={() => onDownload(doc)}
            className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center transition-colors" title="Télécharger"
            data-testid={`download-${doc.id}`}>
            <Download size={16} className="text-slate-500" />
          </button>
          {(canChangeVisibility || canDelete) && (
            <button onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
              className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center transition-colors">
              {isExpanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
            </button>
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-100">
          {doc.description && <p className="text-sm text-slate-600 mb-3">{doc.description}</p>}
          <div className="flex items-center gap-3 flex-wrap">
            {canChangeVisibility && (
              <>
                <select value={doc.visibilite || 'tous'}
                  onChange={e => onVisibilityChange(doc, e.target.value, doc.confidentiel)}
                  className="text-xs px-2 py-1 border border-slate-300 rounded-lg">
                  <option value="tous">Tous</option>
                  <option value="daf_c2">DAF + C2</option>
                  <option value="c2_only">C2 seul</option>
                </select>
                <label className="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="checkbox" checked={doc.confidentiel || false}
                    onChange={e => onVisibilityChange(doc, doc.visibilite, e.target.checked)}
                    className="w-3 h-3 text-red-600 border-slate-300 rounded" />
                  <Lock className="w-3 h-3 text-red-500" /> Confidentiel
                </label>
              </>
            )}
            {canDelete && (
              <button onClick={() => onDelete(doc)}
                className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                data-testid={`delete-${doc.id}`}>
                <Trash2 className="w-3 h-3" /> Supprimer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ============== Upload Form Modal ==============
function UploadFormModal({
  show, onClose, onSubmit, filteredTypes, isC2, uploading,
  selectedFile, setSelectedFile, selectedType, setSelectedType,
  description, setDescription, visibilite, setVisibilite,
  confidentiel, setConfidentiel, fileInputRef, typesDocuments
}) {
  if (!show) return null

  const groupedTypes = filteredTypes.reduce((acc, t) => {
    const cat = t.categorie || 'autre'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(t)
    return acc
  }, {})

  const acceptAll = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.bmp,.svg,.mp4,.mov,.avi,.zip,.rar,.7z"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Ajouter un document</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={20} className="text-slate-500" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fichier *</label>
            <input ref={fileInputRef} type="file" accept={acceptAll}
              onChange={e => setSelectedFile(e.target.files[0])}
              className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              data-testid="file-input" />
            {selectedFile && (
              <p className="mt-1 text-xs text-slate-500">{selectedFile.name} ({formatFileSize(selectedFile.size)})</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type de document</label>
            <select value={selectedType}
              onChange={e => {
                setSelectedType(e.target.value)
                const type = typesDocuments.find(t => t.code === e.target.value)
                if (type) setVisibilite(type.visibilite_defaut || 'tous')
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              data-testid="type-select">
              <option value="">-- Optionnel (auto-détecté) --</option>
              {Object.entries(groupedTypes).map(([cat, types]) => (
                <optgroup key={cat} label={CATEGORY_LABELS[cat] || cat}>
                  {types.map(t => <option key={t.code} value={t.code}>{t.libelle || t.label}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description (optionnel)</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Ex: Facture garage Martin"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          {isC2 && (
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-slate-700 mb-1">Visibilité</label>
                <select value={visibilite} onChange={e => setVisibilite(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="tous">Tous</option>
                  <option value="daf_c2">DAF + C2</option>
                  <option value="c2_only">C2 uniquement</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={confidentiel} onChange={e => setConfidentiel(e.target.checked)}
                    className="w-4 h-4 text-red-600 border-slate-300 rounded focus:ring-red-500" />
                  <Lock className="w-4 h-4 text-red-500" />
                  <span className="text-sm">Confidentiel</span>
                </label>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={!selectedFile || uploading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="submit-upload-btn">
              {uploading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Envoi...</>) : (<><Check className="w-4 h-4" /> Envoyer</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
